// Shared engine behind the Command Center layouts (Strip board, Riso press):
// the brief split into fires / today / watch, optimistic snooze, dismiss and
// undo, changes since the last visit, and keyboard triage over [data-strip].
import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProductBrain } from '../contexts/ProductBrainContext';
import { useGuide } from '../contexts/GuideContext';
import { buildBrief, buildGraph } from '../lib/productInsights';
import { services } from '../services/productBrain';

export const DAY = 86400000;
export const STAMP_MS = 6000;
const VISIT_KEY = 'spcc:cc-last-visit';
const BRIEF_KEY = 'spcc:cc-last-brief';
const SESSION_KEY = 'spcc:cc-visit-baseline';


export const TYPE_NAMES = { issues: 'issue', initiatives: 'initiative', problems: 'problem', evidence: 'evidence', decisions: 'decision', experiments: 'experiment', results: 'result', learnings: 'learning', hypotheses: 'hypothesis', objectives: 'objective', inbox: 'signal', reminders: 'reminder' };

export const stamp = r => r && (r.updated_at || r.updated_date || r.created_at || r.created_date);
export const born = r => r && (r.created_at || r.created_date);
export const safeName = id => `strip-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
export const shortDate = iso => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
export const humanDates = text => String(text || '').replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (_, iso) => shortDate(iso));
export const verb = action => String(action || 'Open').split(' ')[0];
export const isTyping = el => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

function readStorage(store, key) { try { return store.getItem(key); } catch { return null; } }
function writeStorage(store, key, value) { try { store.setItem(key, value); } catch { /* storage unavailable */ } }

// The baseline for "since you were here" is fixed once per browser session, so
// moving between pages does not reset it.
function readBaseline() {
  const pinned = readStorage(sessionStorage, SESSION_KEY);
  if (pinned) { try { return JSON.parse(pinned); } catch { /* fall through */ } }
  let ids = null;
  try { ids = JSON.parse(readStorage(localStorage, BRIEF_KEY) || 'null'); } catch { /* ignore */ }
  const baseline = { at: readStorage(localStorage, VISIT_KEY), ids };
  writeStorage(sessionStorage, SESSION_KEY, JSON.stringify(baseline));
  writeStorage(localStorage, VISIT_KEY, new Date().toISOString());
  return baseline;
}

// Elapsed time; empty for a missing or future timestamp rather than a guess.
export function age(iso, now) {
  if (!iso) return '';
  const ms = now - new Date(iso);
  if (Number.isNaN(ms) || ms < 0) return '';
  if (ms < 3600000) return `${Math.max(1, Math.round(ms / 60000))}m`;
  if (ms < DAY) return `${Math.round(ms / 3600000)}h`;
  return `${Math.round(ms / DAY)}d`;
}
export function clock(iso, now) {
  const d = new Date(iso);
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (now - d < 6 * DAY) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
export const sinceLabel = iso => `${new Date(iso).toLocaleDateString(undefined, { weekday: 'long' })} ${new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;

// The fact that matters for each kind of prompt, shown in the strip's type field.
export function metaFor(prefix, item, record, now) {
  const today = now.toISOString().slice(0, 10);
  const days = iso => Math.round((new Date(`${today}T12:00:00`) - new Date(`${iso}T12:00:00`)) / DAY);
  const match = re => item.detail?.match(re)?.[1];
  if (prefix === 'issue') {
    const open = age(born(record) || stamp(record), now);
    return [record?.jira_key || record?.key || record?.external_id, open && `Open ${open}`].filter(Boolean).join(' · ');
  }
  if (prefix === 'readiness') return match(/Readiness (\d+)%/) ? `Readiness ${match(/Readiness (\d+)%/)}%` : '';
  if (prefix === 'validated' || prefix === 'stronger') return match(/Evidence score (\d+)/) ? `Score ${match(/Evidence score (\d+)/)}/100` : '';
  if (prefix === 'stale') return match(/(\d+) days old/) ? `Evidence ${match(/(\d+) days old/)}d old` : '';
  if (prefix === 'decision' && record?.review_date) { const late = days(record.review_date); return `Due ${shortDate(record.review_date)}${late > 0 ? ` · ${late}d overdue` : ''}`; }
  if (prefix === 'experiment' && record?.end_date) return `Ended ${shortDate(record.end_date)}`;
  return '';
}

// What changed for a record since the baseline, in the product's own terms.
export function changeFor(node, since, evidenceCount) {
  const created = since && born(node) && new Date(born(node)) > since;
  if (node.type === 'issues') return created ? `New ${Number(node.tier) === 1 ? 'T1 ' : ''}issue${node.source && node.source !== 'manual' ? ` from ${node.source[0].toUpperCase()}${node.source.slice(1)}` : ''}` : `Issue updated · ${node.status || 'open'}`;
  if (node.type === 'inbox') return 'New signal in Inbox';
  if (node.type === 'problems' && evidenceCount) return `+${evidenceCount} evidence${created ? ' · new problem' : ''}`;
  if (node.type === 'initiatives') return created ? `Added to ${node.time_horizon || 'roadmap'}` : `Updated · ${[node.time_horizon, String(node.status || '').replace(/_/g, ' ')].filter(Boolean).join(', ')}`;
  return created ? `New ${TYPE_NAMES[node.type] || node.type}` : `${(TYPE_NAMES[node.type] || node.type).replace(/^./, c => c.toUpperCase())} updated`;
}

// Brief ids are "<kind>:<record id>"; the kind decides which records to read.
// How urgent a prompt is, the only thing layouts color by:
// critical = a fire or an overdue decision review; attention = due now,
// incomplete, outranking current work or stale; normal = everything else.
export function urgencyOf(prefix, record, now = new Date()) {
  if (prefix === 'issue') return 'critical';
  if (prefix === 'decision') return record?.review_date && record.review_date < now.toISOString().slice(0, 10) ? 'critical' : 'attention';
  if (['readiness', 'stronger', 'experiment', 'stale'].includes(prefix)) return 'attention';
  return 'normal';
}

// The area a prompt or record belongs to; layouts theme by area.
export const AREA_OF_KIND = { issue: 'fires', decision: 'decisions', readiness: 'initiatives', validated: 'discovery', stronger: 'discovery', stale: 'discovery', experiment: 'learning' };
export const AREA_OF_TYPE = { issues: 'fires', decisions: 'decisions', initiatives: 'initiatives', objectives: 'initiatives', problems: 'discovery', evidence: 'discovery', hypotheses: 'learning', experiments: 'learning', results: 'learning', learnings: 'learning', inbox: 'inbox' };

export const BASE_KINDS = {
  issue: { type: 'issues', label: 'Critical issue' },
  readiness: { type: 'initiatives', label: 'Initiative' },
  validated: { type: 'problems', label: 'Problem' },
  stronger: { type: 'problems', label: 'Problem' },
  stale: { type: 'problems', label: 'Stale evidence' },
  decision: { type: 'decisions', label: 'Decision review' },
  experiment: { type: 'experiments', label: 'Experiment' },
};
const FALLBACK_KIND = { type: null, label: 'Prompt' };

export function useMorningBrief({ kinds = BASE_KINDS, movingClass = 'cc-moving' } = {}) {
  const { data, loading: fetching, workspace } = useProductBrain();
  // Only the first load shows placeholders. Background refreshes keep the lists
  // on screen, so rows never remount (and never replay their entrance) on a sync.
  const loadedOnce = useRef(false);
  if (!fetching) loadedOnce.current = true;
  const loading = fetching && !loadedOnce.current;
  const guide = useGuide();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [baseline] = useState(readBaseline);
  const [local, setLocal] = useState([]); // optimistic feedback records
  const [deleted, setDeleted] = useState(() => new Set());
  const [stamped, setStamped] = useState(() => new Set());
  const [busy, setBusy] = useState(() => new Set());
  const [error, setError] = useState('');
  const timers = useRef({});
  const lastDismissed = useRef(null);
  const now = new Date();

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  // Pointer actions move strips with a view transition; keyboard actions never animate.
  const move = (update, animate = true) => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduce || !document.startViewTransition) return update();
    document.documentElement.classList.add(movingClass);
    const transition = document.startViewTransition(() => flushSync(update));
    transition.finished.finally(() => document.documentElement.classList.remove(movingClass));
  };

  const brief = useMemo(() => buildBrief(data, new Date()), [data]);
  useEffect(() => { if (!loading) writeStorage(localStorage, BRIEF_KEY, JSON.stringify(brief.map(i => i.id))); }, [brief, loading]);

  const previousIds = params.get('since') === 'last' ? guide?.activation?.previous_brief_ids || [] : baseline.ids;
  const feedback = useMemo(() => {
    const seen = new Set();
    return [...local, ...data.feedback].filter(f => !deleted.has(f.id) && !seen.has(f.id) && seen.add(f.id));
  }, [local, data.feedback, deleted]);

  const statusOf = item => {
    const own = feedback.filter(f => f.insight_key === item.id);
    const dismissal = own.find(f => f.action === 'dismiss');
    if (dismissal) return stamped.has(item.id) ? { stamped: true, recordId: String(dismissal.id).startsWith('local:') ? null : dismissal.id } : null;
    const snooze = own.find(f => f.snoozed_until && new Date(f.snoozed_until) > now);
    return snooze ? { snoozedUntil: snooze.snoozed_until, records: own.filter(f => f.action === 'snooze') } : {};
  };

  const strips = brief.map(item => {
    const prefix = item.id.split(':')[0];
    const kind = { prefix, ...(kinds[prefix] || FALLBACK_KIND) };
    const recordId = item.id.slice(prefix.length + 1);
    const record = kind.type ? data[kind.type]?.find(r => String(r.id) === recordId) : null;
    return { item, kind, record, urgency: urgencyOf(prefix, record, now), meta: metaFor(prefix, item, record, now), status: statusOf(item), isNew: Boolean(previousIds && !previousIds.includes(item.id)) };
  }).filter(s => s.status);

  const fires = strips.filter(s => s.item.priority === 0 && !s.status.snoozedUntil);
  const today = strips.filter(s => [1, 2].includes(s.item.priority) && !s.status.snoozedUntil);
  const watch = strips.filter(s => s.item.priority >= 3 || s.status.snoozedUntil);
  const clearedToday = feedback.filter(f => f.action === 'dismiss' && born(f) && new Date(born(f)).toDateString() === now.toDateString()).length;
  const firstId = (fires[0] || today[0] || watch[0])?.item.id;
  const byId = Object.fromEntries(strips.map(s => [s.item.id, s]));

  const arrivals = useMemo(() => {
    const since = baseline.at ? new Date(baseline.at) : null;
    const after = iso => iso && (!since || new Date(iso) > since) && new Date(iso) <= new Date();
    const nodes = buildGraph(data).nodes.filter(n => n.type !== 'feedback' && n.type !== 'evidence');
    const evidence = {};
    for (const e of data.evidence) if (since && after(born(e))) {
      const key = String(e.problem_card_id);
      evidence[key] = { count: (evidence[key]?.count || 0) + 1, at: [evidence[key]?.at, born(e)].filter(Boolean).sort().pop() };
    }
    const todayIso = new Date().toISOString().slice(0, 10);
    const dateCrossed = date => since && date && date <= todayIso && date > since.toISOString().slice(0, 10);
    const rows = [];
    for (const n of nodes) {
      const label = n.title || n.statement || n.description || n.content?.slice(0, 90) || n.label;
      const ev = n.type === 'problems' ? evidence[String(n.id)] : null;
      if (n.type === 'decisions' && dateCrossed(n.review_date) && n.status !== 'reviewed') rows.push({ ...n, label, at: `${n.review_date}T00:00:00`, change: 'Review date passed' });
      else if (n.type === 'experiments' && dateCrossed(n.end_date) && n.status !== 'completed') rows.push({ ...n, label, at: `${n.end_date}T00:00:00`, change: 'Reached its end date' });
      else if (ev || after(stamp(n))) rows.push({ ...n, label, at: [ev?.at, after(stamp(n)) ? stamp(n) : null].filter(Boolean).sort().pop(), change: changeFor(n, since, ev?.count) });
    }
    return rows.sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 8);
  }, [data, baseline.at]);

  const track = (id, on) => setBusy(prev => { const nextSet = new Set(prev); on ? nextSet.add(id) : nextSet.delete(id); return nextSet; });
  const fail = e => setError(`That change was not saved: ${e.message || e}. Try again.`);
  const unstamp = id => setStamped(prev => { const nextSet = new Set(prev); nextSet.delete(id); return nextSet; });

  async function record(item, action, animate = true) {
    const temp = { id: `local:${item.id}:${Date.now()}`, insight_key: item.id, action, snoozed_until: action === 'snooze' ? new Date(Date.now() + DAY).toISOString() : null, created_at: new Date().toISOString() };
    setError('');
    track(item.id, true);
    move(() => {
      setLocal(prev => [temp, ...prev]);
      if (action === 'dismiss') setStamped(prev => new Set(prev).add(item.id));
    }, animate);
    if (action === 'dismiss') {
      lastDismissed.current = item.id;
      timers.current[item.id] = setTimeout(() => move(() => unstamp(item.id)), STAMP_MS);
    }
    try {
      const saved = await services.feedback.create({ insight_key: temp.insight_key, action, snoozed_until: temp.snoozed_until });
      setLocal(prev => prev.map(f => f.id === temp.id ? { ...temp, ...saved } : f));
    } catch (e) {
      clearTimeout(timers.current[item.id]);
      move(() => { setLocal(prev => prev.filter(f => f.id !== temp.id)); unstamp(item.id); }, animate);
      fail(e);
    } finally { track(item.id, false); }
  }

  async function remove(item, ids, animate = true) {
    setError('');
    track(item.id, true);
    clearTimeout(timers.current[item.id]);
    try {
      await Promise.all(ids.filter(id => id && !String(id).startsWith('local:')).map(id => services.feedback.delete(id)));
      move(() => {
        setDeleted(prev => { const nextSet = new Set(prev); ids.forEach(id => nextSet.add(id)); return nextSet; });
        unstamp(item.id);
      }, animate);
    } catch (e) { fail(e); } finally { track(item.id, false); }
  }

  // Triage from the keyboard: J/K move, Enter opens, S snoozes, E dismisses, Z undoes.
  const onKeyDown = e => {
    if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
    const els = [...((e.currentTarget || document).querySelectorAll('[data-strip]') || [])];
    const current = e.target.closest?.('[data-strip]');
    const index = els.indexOf(current);
    const key = e.key.toLowerCase();
    if (key === 'j' || key === 'k') {
      e.preventDefault();
      const nextIndex = index < 0 ? 0 : Math.min(els.length - 1, Math.max(0, index + (key === 'j' ? 1 : -1)));
      els[nextIndex]?.focus();
      return;
    }
    if (key === 'z' && lastDismissed.current) {
      const s = byId[lastDismissed.current];
      if (s?.status.stamped && s.status.recordId) { e.preventDefault(); remove(s.item, [s.status.recordId], false); }
      return;
    }
    const s = current && byId[current.dataset.strip];
    if (!s || s.status.stamped || current !== e.target) return;
    if (key === 'enter') { e.preventDefault(); guide?.act(s.item.id); navigate(s.item.href); }
    else if (key === 's' && !s.status.snoozedUntil) { e.preventDefault(); record(s.item, 'snooze', false); }
    else if (key === 'e') { e.preventDefault(); els[index + 1]?.focus(); record(s.item, 'dismiss', false); }
  };

  const sinceText = baseline.at ? sinceLabel(baseline.at) : null;
  return { data, loading, workspace, guide, now, baseline, sinceText, error, busy, strips, fires, today, watch, clearedToday, firstId, arrivals, record, remove, onKeyDown };
}
